<template>
	<div class="progress-dial" :style="{ '--progress-colour': activeColour }">
		<!-- Main card section -->
		<div class="progress-dial-main">
			<!-- Header: title left, count right -->
			<div v-if="title" class="progress-dial-header">
				<span class="progress-dial-title">{{ title }}</span>
				<span class="progress-dial-title">{{ displayText }}</span>
			</div>

			<!-- Gauge -->
			<div class="progress-dial-gauge">
				<svg viewBox="0 0 200 108" class="progress-dial-svg">
					<!-- Background arc (full semicircle) -->
					<path
						d="M 8 100 A 92 92 0 0 1 192 100"
						fill="none"
						stroke="#F5F4F6"
						stroke-width="5"
						stroke-linecap="round"
					/>
					<!-- Progress arc (animated) -->
					<path
						d="M 8 100 A 92 92 0 0 1 192 100"
						fill="none"
						class="progress-arc"
						:stroke="barColour"
						:stroke-dasharray="arcLength"
						:stroke-dashoffset="currentOffset"
					/>
					<!-- Dot at end of arc -->
					<circle
						cx="192"
						cy="100"
						r="5"
						fill="white"
						:stroke="currentProgress >= 1 ? barColour : '#F5F4F6'"
						stroke-width="4"
					/>
					<!-- Dot at current progress position -->
					<circle
						v-if="currentProgress > 0.01"
						:cx="progressDotPosition.x"
						:cy="progressDotPosition.y"
						r="5"
						:fill="barColour"
					/>
				</svg>
				<div class="progress-dial-value" @click="animate">{{ displayNumber }}</div>
			</div>

			<!-- Date labels -->
			<div class="progress-dial-labels">
				<span class="progress-dial-label">{{ startLabel }}</span>
				<span class="progress-dial-label">{{ endLabel }}</span>
			</div>

			<!-- Divider + Message -->
			<div v-if="message" class="progress-dial-message-section">
				<div class="progress-dial-divider"></div>
				<div class="progress-dial-message">
					<div v-if="isAllSet" class="progress-dial-check-icon"></div>
					<img v-else src="@/assets/infooutline.png" class="progress-dial-info-icon" />
					<span class="progress-dial-message-text" v-html="message"></span>
				</div>
			</div>
		</div>

		<!-- Footer section -->
		<div v-if="footerLabel" class="progress-dial-footer">
			<span class="progress-dial-footer-text">{{ footerLabel }}</span>
			<span class="progress-dial-footer-text">{{ footerValue }}</span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ProgressDial',
	props: {
		fromDate: { type: String, required: true },
		toDate: { type: String, required: true },
		title: { type: String, default: '' },
		footerLabel: { type: String, default: '' },
		animationDuration: { type: Number, default: 1500 }
	},
	data() {
		return {
			currentProgress: 0,
			displayNumber: 0,
			arcLength: Math.PI * 92
		};
	},
	computed: {
		total() {
			const from = new Date(this.fromDate);
			const to = new Date(this.toDate);
			return Math.round((to - from) / (1000 * 60 * 60 * 24));
		},
		value() {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const to = new Date(this.toDate);
			return Math.max(0, Math.round((to - today) / (1000 * 60 * 60 * 24)));
		},
		startLabel() {
			return this.formatDate(this.fromDate);
		},
		endLabel() {
			return this.formatDate(this.toDate);
		},
		footerValue() {
			return this.formatDate(this.toDate);
		},
		targetProgress() {
			if (this.total <= 0) return 0;
			return Math.max(0, Math.min(1, (this.total - this.value) / this.total));
		},
		currentOffset() {
			return this.arcLength * (1 - this.currentProgress);
		},
		progressDotPosition() {
			const angle = Math.PI * (1 - this.currentProgress);
			return {
				x: 100 + 92 * Math.cos(angle),
				y: 100 - 92 * Math.sin(angle)
			};
		},
		barColour() {
			return this.value <= 30 ? '#E53935' : '#7FD038';
		},
		activeColour() {
			return this.barColour;
		},
		displayText() {
			return this.value <= 0 ? 'Expired' : this.displayNumber;
		},
		isAllSet() {
			return this.targetProgress < 1 && this.value > 30;
		},
		message() {
			if (this.targetProgress >= 1) return '<b>Policy has expired</b> – you may no longer be covered. Compare new policies.';
			if (this.value <= 30) return '<b>Policy renews soon</b> – start reviewing your cover so you\'re ready to switch or renew.';
			return '<b>You\'re all set</b> – there\'s plenty of time before you\'ll need to review this policy.';
		}
	},
	mounted() {
		this.animate();
	},
	methods: {
		formatDate(dateStr) {
			const date = new Date(dateStr);
			return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
		},
		animate() {
			const startTime = performance.now();
			const targetProgress = this.targetProgress;
			const targetValue = this.value;
			const duration = this.animationDuration;

			const step = (currentTime) => {
				const elapsed = currentTime - startTime;
				const t = Math.min(elapsed / duration, 1);
				const eased = 1 - Math.pow(1 - t, 3);

				this.currentProgress = targetProgress * eased;
				this.displayNumber = Math.round(this.total - (this.total - targetValue) * eased);

				if (t < 1) {
					requestAnimationFrame(step);
				}
			};

			requestAnimationFrame(step);
		}
	},
	watch: {
		fromDate() { this.animate(); },
		toDate() { this.animate(); }
	}
};
</script>

<style scoped>
.progress-dial {
	display: flex;
	flex-direction: column;
	gap: 1px;
	background: #F5F4F6;
	border-radius: 8px;
}

.progress-dial-main {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	padding: 16px;
	background: white;
	border-radius: 8px 8px 0 0;
}

.progress-dial-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.progress-dial-title {
	font-size: 13px;
	color: var(--brand-purple);
}

.progress-dial-gauge {
	position: relative;
	width: 75%;
	aspect-ratio: 200 / 108;
}

.progress-dial-svg {
	width: 100%;
	height: 100%;
}

.progress-arc {
	stroke-width: 5;
	stroke-linecap: round;
}

.progress-dial-value {
	position: absolute;
	top: 58%;
	left: 50%;
	transform: translate(-50%, -50%);
	font-size: 40px;
	font-weight: bold;
	color: var(--brand-purple);
	text-align: center;
	line-height: 1;
}

.progress-dial-labels {
	display: flex;
	justify-content: space-between;
	width: 75%;
	margin-top: -16px;
}

.progress-dial-label {
	font-size: 11px;
	color: var(--brand-purple);
	opacity: 0.5;
}

.progress-dial-message-section {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
}

.progress-dial-divider {
	height: 1px;
	background: #F9F7F3;
}

.progress-dial-message {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px;
	border-radius: 4px;
}

.progress-dial-check-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	-webkit-mask-image: url(~@/assets/CheckIcon.png);
	mask-image: url(~@/assets/CheckIcon.png);
	-webkit-mask-size: contain;
	mask-size: contain;
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	background-color: var(--progress-colour);
}

.progress-dial-info-icon {
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.progress-dial-message-text {
	font-size: 11px;
	color: var(--brand-purple);
}

.progress-dial-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 16px;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 0 0 8px 8px;
}

.progress-dial-footer-text {
	font-size: 13px;
	color: var(--brand-purple);
}
</style>
